import { useCallback } from 'react';
import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';

import type { ApiDocument, GetDocumentsResponse } from '@izimi/api';
import { chunkArray } from '@izimi/utils';

import type DocumentEntity from './DocumentEntity';
import type { DocumentsQueryKeyType } from './DocumentsQueryKeys';
import DocumentsQueryKeys from './DocumentsQueryKeys';

export const documentsQueryKeys = new DocumentsQueryKeys('documents');

const invalidateDocumentsCache = (type: DocumentsQueryKeyType) => () => {
  const queryClient = useQueryClient();

  const invalidateDocumentsVaultListCache = useCallback(() => {
    return queryClient.invalidateQueries({
      queryKey: documentsQueryKeys.getDocuments(type),
    });
  }, [queryClient]);

  return invalidateDocumentsVaultListCache;
};

export const useInvalidateDocumentsVaultListCache = invalidateDocumentsCache('vaultList');

export const useInvalidateDocumentsUnSeenListCache = invalidateDocumentsCache('unSeen');

export const useInvalidateDocumentsTrashListCache = invalidateDocumentsCache('trashList');

export const useInvalidateDocumentsNabanListCache = invalidateDocumentsCache('nabanList');

export const useInvalidateDocumentsInheritedListCache = invalidateDocumentsCache('inheritedList');

export const useInvalidateDocumentsSharedByYouListCache =
  invalidateDocumentsCache('sharedByYouList');

export const useInvalidateDocumentsSharedToYouListCache =
  invalidateDocumentsCache('sharedToYouList');

export const useInvalidateDocumentsSharedFromNotaryListCache =
  invalidateDocumentsCache('sharedFromNotaryList');

export const useInvalidateDocumentsSearchCache = invalidateDocumentsCache('searchDocuments');

export const useInvalidateDocumentsSelectionCache = invalidateDocumentsCache('selectDocuments');

export const useInvalidateDocumentsVaultTransferCache = invalidateDocumentsCache('vaultTransfer');

export const useInvalidateDocumentsPreviewCache = invalidateDocumentsCache('preview');

export const useInvalidateDocumentCache = () => {
  const queryClient = useQueryClient();

  const invalidateDocumentCache = useCallback(
    (id: string) =>
      queryClient.invalidateQueries({
        queryKey: documentsQueryKeys.getDocument(id),
      }),
    [queryClient],
  );

  return invalidateDocumentCache;
};

export const useInvalidateDocumentsCache = () => {
  const invalidateDocumentCache = useInvalidateDocumentCache();

  const invalidateDocumentsCache = useCallback(
    (ids: Array<string>) => ids.forEach(invalidateDocumentCache),
    [invalidateDocumentCache],
  );

  return invalidateDocumentsCache;
};

export const useSetDocumentCache = () => {
  const queryClient = useQueryClient();

  const setDocumentCache = useCallback(
    (doc: DocumentEntity) => queryClient.setQueryData(documentsQueryKeys.getDocument(doc.id), doc),
    [queryClient],
  );

  return setDocumentCache;
};

type T = InfiniteData<GetDocumentsResponse>;

export const useUpdatePagedDocumentsCache = () => {
  const queryClient = useQueryClient();

  const updatePagedDocumentsCache = useCallback(
    (type: DocumentsQueryKeyType, cb: (allDocuments: ApiDocument[], data: T) => ApiDocument[]) => {
      queryClient.setQueriesData<T | undefined>(
        { queryKey: documentsQueryKeys.getDocuments(type) },
        (data) => {
          if (data) {
            const allDocuments = data.pages.flatMap((page) => page.documents);
            const updatedDocuments = cb(allDocuments, data);

            if (updatedDocuments.length === 0) {
              return {
                pageParams: [],
                pages: [
                  {
                    documents: [],
                    total: 0,
                  },
                ],
              };
            }

            const pages = chunkArray(updatedDocuments, 15).map((chunk) => ({
              documents: chunk,
              total: (data.pages[0]?.total ?? 0) - (allDocuments.length - updatedDocuments.length),
            }));
            return {
              pageParams: data.pageParams,
              pages,
            };
          }
          return data;
        },
      );
    },
    [queryClient],
  );

  return updatePagedDocumentsCache;
};
